import * as React from "react"
import { useNavigate } from "react-router-dom"

import { format } from "date-fns"

import createBlogPost from "../../../../api/blogposts/create"
// import FilterContainer from '../../../../components/FilterContainer'
import searchBlogPosts from "../../../../api/blogposts/search"
import Copyright from "../../../../components/Copyright"
import HeadingContainer from "../../../../components/HeadingContainer"
import { BlogPost } from "../../../../types/BlogPost"
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link,
  LoadingButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import EditIcon from "@podcastsoundboard/ps-lib/ui/icons/Edit"
import VisibilityIcon from "@podcastsoundboard/ps-lib/ui/icons/Visibility"

// import { darkTheme, lightTheme } from '../../../../lib/muiTheme'

let queryTimeout: NodeJS.Timeout

const PER_PAGE = 10

export default function AdminBlogPostManagement() {
  const navigate = useNavigate()
  // const darkModeEnabled = useAppSelector(state => state.darkModeEnabled)

  const [createLoading, setCreateLoading] = React.useState(false)
  const [blogPostsLoading, setBlogPostsLoading] = React.useState(true)
  const [blogPosts, setBlogPosts] = React.useState<BlogPost[] | null>(null)
  const [page, setPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(0)
  const [errorMessage, setErrorMessage] = React.useState("")

  const handleSearchBlogPosts = React.useCallback(() => {
    searchBlogPosts({
      page,
      perPage: PER_PAGE,
    })
      .then(({ blogPosts, totalPages }) => {
        setBlogPosts(blogPosts)
        setTotalPages(totalPages)
        setBlogPostsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setBlogPostsLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }, [page])

  const handleCreateBlogPost = () => {
    setCreateLoading(true)
    createBlogPost({
      title: "No name",
      subtitle: "No subtitle",
    })
      .then((blogPost) => {
        setCreateLoading(false)
        navigate(`/app/admin/blogposts/${blogPost.uuid}`)
      })
      .catch((err) => {
        setCreateLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }

  React.useEffect(() => {
    setBlogPostsLoading(true)
    clearTimeout(queryTimeout)
    queryTimeout = setTimeout(() => {
      handleSearchBlogPosts()
    }, 300)
  }, [page, handleSearchBlogPosts])

  return (
    <div>
      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity="error">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <HeadingContainer>
        <Typography component="h1" variant="h6" noWrap color="textPrimary">
          Blog Posts
        </Typography>

        <LoadingButton
          variant="contained"
          onClick={handleCreateBlogPost}
          loading={createLoading}
        >
          Create Post
        </LoadingButton>
      </HeadingContainer>

      <Divider className="mb-4" />

      {blogPostsLoading && (
        <Box className="flex justify-center">
          <CircularProgress />
        </Box>
      )}

      {!blogPostsLoading && (
        <Container maxWidth={false}>
          <Pagination
            className="mb-6"
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
          />

          <TableContainer component={Paper}>
            <Table className="min-w-[650px]" size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <b>Created At</b>
                  </TableCell>
                  <TableCell align="left">
                    <b>Title</b>
                  </TableCell>
                  <TableCell align="left">
                    <b>Hidden</b>
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {blogPosts &&
                  blogPosts.map((blogPost) => (
                    <TableRow key={blogPost.uuid}>
                      <TableCell align="left">
                        {format(new Date(blogPost.createdAt), "Pp")}
                      </TableCell>

                      <TableCell align="left">
                        {blogPost.title || <i>None</i>}
                      </TableCell>
                      <TableCell align="left">
                        {blogPost.hidden ? "Hidden" : "Public"}
                      </TableCell>
                      <TableCell align="right">
                        <Box className="pr-4">
                          <Tooltip title="View">
                            <Link
                              href={`/app/blog/${blogPost.uuid}`}
                              onClick={(e) => e.preventDefault()}
                            >
                              <IconButton
                                onClick={() =>
                                  navigate(`/app/blog/${blogPost.uuid}`)
                                }
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Link>
                          </Tooltip>

                          <Tooltip title="Edit">
                            <Link
                              href={`/app/admin/blogposts/${blogPost.uuid}`}
                              onClick={(e) => e.preventDefault()}
                            >
                              <IconButton
                                onClick={() =>
                                  navigate(
                                    `/app/admin/blogposts/${blogPost.uuid}`,
                                  )
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Link>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      )}

      <Copyright className="pt-8" />
    </div>
  )
}
